import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { StatusBadge } from '../components/StatusBadge';
import { Clock, Target, TrendingUp, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SummaryPage() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchAssignments();
    }
  }, [user?.id]);

  const fetchAssignments = async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API_URL}/api/assignments/user/${user.id}`);
      setAssignments(response.data);
    } catch (error) {
      toast.error('Failed to load summary data');
    } finally {
      setLoading(false);
    }
  };

  const totalAllocated = assignments.reduce((sum, a) => sum + a.allocated_hours_total, 0);
  const totalLogged = assignments.reduce((sum, a) => sum + a.logged_hours, 0);
  const totalRemaining = totalAllocated - totalLogged;
  const overallProgress = totalAllocated > 0 ? (totalLogged / totalAllocated) * 100 : 0;

  const statusCounts = {
    on_track: assignments.filter(a => a.status === 'on_track').length,
    at_risk: assignments.filter(a => a.status === 'at_risk').length,
    over: assignments.filter(a => a.status === 'over').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Allocated</CardTitle>
            <Target className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalAllocated}h</div>
            <p className="text-xs text-muted-foreground mt-1">Across {assignments.length} projects</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Hours Logged</CardTitle>
            <Clock className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{totalLogged.toFixed(1)}h</div>
            <Progress value={overallProgress} className="mt-2 h-2" />
            <p className="text-xs text-muted-foreground mt-1">{overallProgress.toFixed(0)}% complete</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Remaining</CardTitle>
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${totalRemaining < 0 ? 'text-red-600' : ''}`}>
              {totalRemaining.toFixed(1)}h
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {totalRemaining >= 0 ? 'Budget remaining' : 'Over budget'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Status Overview</CardTitle>
            <AlertTriangle className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-sm font-medium">{statusCounts.on_track}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-sm font-medium">{statusCounts.at_risk}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-sm font-medium">{statusCounts.over}</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">On track / At risk / Over</p>
          </CardContent>
        </Card>
      </div>

      {/* Project Details */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold tracking-tight">Project Allocations</h2>
        
        {assignments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No project assignments found.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {assignments.map((assignment) => {
              const progress = assignment.allocated_hours_total > 0 
                ? (assignment.logged_hours / assignment.allocated_hours_total) * 100 
                : 0;

              return (
                <Card key={assignment.id} className="hover:shadow-md transition-shadow" data-testid={`project-card-${assignment.project_id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{assignment.project_name}</CardTitle>
                        <CardDescription>{assignment.project_code}</CardDescription>
                      </div>
                      <StatusBadge status={assignment.status} />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-medium">{progress.toFixed(0)}%</span>
                      </div>
                      <Progress value={Math.min(progress, 100)} className="h-2" />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 pt-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Allocated</p>
                        <p className="text-lg font-semibold">{assignment.allocated_hours_total}h</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Logged</p>
                        <p className="text-lg font-semibold text-primary">{assignment.logged_hours.toFixed(1)}h</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Remaining</p>
                        <p className={`text-lg font-semibold ${assignment.remaining_hours < 0 ? 'text-red-600' : ''}`}>
                          {assignment.remaining_hours.toFixed(1)}h
                        </p>
                      </div>
                    </div>

                    {assignment.allocation_start_date && assignment.allocation_end_date && (
                      <div className="pt-2 border-t border-border">
                        <p className="text-xs text-muted-foreground">
                          Allocation period: {assignment.allocation_start_date} to {assignment.allocation_end_date}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
